import { formatFileSize } from '@/shared/lib';

export interface UploadFileRules {
  /** Allowed file extensions, e.g. `['.xlsx', '.pdf']`. Empty / undefined = any type. */
  allowedExtensions?: string[];
  /** Maximum file size in bytes. Undefined = no limit. */
  maxSize?: number;
}

export interface UploadFileValidation {
  valid: boolean;
  error?: string;
}

/** Lower-cases and ensures a leading dot: `XLSX` / `.XLSX` → `.xlsx`. */
export const normalizeExtension = (ext: string): string => {
  const lower = ext.trim().toLowerCase();
  return lower.startsWith('.') ? lower : `.${lower}`;
};

/** Builds an HTML `accept` value from an extension allowlist (`['.xlsx']` → `.xlsx`). */
export const extensionsToAccept = (allowedExtensions?: string[]): string | undefined =>
  allowedExtensions && allowedExtensions.length > 0 ? allowedExtensions.map(normalizeExtension).join(',') : undefined;

/**
 * Validates a single file against optional extension and size rules. With no
 * rules provided, every file is valid. Extension is checked before size.
 */
export const validateUploadFile = (file: File, rules: UploadFileRules = {}): UploadFileValidation => {
  const { allowedExtensions, maxSize } = rules;

  if (allowedExtensions && allowedExtensions.length > 0) {
    const name = file.name.toLowerCase();
    const allowed = allowedExtensions.map(normalizeExtension);
    if (!allowed.some((ext) => name.endsWith(ext))) {
      return { valid: false, error: `Unsupported file type. Allowed: ${allowed.join(', ')}` };
    }
  }

  if (typeof maxSize === 'number' && file.size > maxSize) {
    return { valid: false, error: `File is too large. Maximum size is ${formatFileSize(maxSize)}.` };
  }

  return { valid: true };
};
